import type { ApprovalAttributes } from '@/types/approval';

/** 인플루언서 월마감 보고서(108) — 레거시 appr_edit_forms_influencer_monthly. 인플루언서 시스템 `Ep` 진입 전용 */
export const INFLUENCER_MONTHLY_REPORT_TYPE = 108;

export const isInfluencerMonthlyReportType = (apvType: number): boolean =>
  apvType === INFLUENCER_MONTHLY_REPORT_TYPE;

/** 인플루언서 시스템이 문서 일련번호를 `Ep` 로 넘김 */
export const INFLUENCER_MONTHLY_ENTRY_QUERY_KEY = 'Ep';

export const INFLUENCER_MONTHLY_LOAD_FAILED_MESSAGE =
  '인플루언서 월마감 보고서 로딩에 실패하였습니다.';
export const INFLUENCER_MONTHLY_MISSING_MESSAGE =
  '인플루언서 월마감 리포트 로드에 실패하였습니다.';
export const INFLUENCER_MONTHLY_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';

export type InfluencerPerformance = {
  name?: string;
  contentsCount?: number | string;
  engagement?: number | string;
  tagPriceSum?: number | string;
};

export type InfluencerMonthlyReport = {
  brandName?: string;
  year?: string;
  month?: string;
  categories?: string[];
  domains?: string[];
  exposures?: { channelName?: string; contentsCount?: number | string }[];
  cost?: {
    productCount?: number | string;
    distCount?: number | string;
    tagPriceSum?: number | string;
    tagPricePortion?: number | string;
  };
  channelPerformances?: InfluencerPerformance[];
  domainPerformances?: InfluencerPerformance[];
  categoryPerformances?: InfluencerPerformance[];
};

export type InfluencerMonthlyDraftSource = {
  code: number;
  attributes?: { documentSequence?: string };
  display?: { source?: InfluencerMonthlyReport | null };
};

export type InfluencerMonthlyReportState = {
  documentSequence: string | null;
  report: InfluencerMonthlyReport | null;
  loading: boolean;
  /** 조회로 채운 제목 — 이탈 경고 판정에서 사용자 입력으로 보지 않음 */
  autoSubject?: string;
};

export const createInfluencerMonthlyReportState = (
  documentSequence: string | null = null,
): InfluencerMonthlyReportState => ({
  documentSequence,
  report: null,
  loading: false,
});

export const pickInfluencerMonthlyReport = (
  source: InfluencerMonthlyReport | null | undefined,
): InfluencerMonthlyReport | null =>
  source && typeof source === 'object' && !Array.isArray(source)
    ? source
    : null;

export const getInfluencerMonthlyEntrySequence = (
  query: Record<string, string | string[] | undefined>,
): string | undefined => {
  const raw = query[INFLUENCER_MONTHLY_ENTRY_QUERY_KEY];
  const value = (Array.isArray(raw) ? raw[0] : raw)?.trim();
  return value || undefined;
};

export const parseInfluencerMonthlySequence = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const value = attributes?.documentSequence;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export const parseInfluencerMonthlyContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => {
  const content = attributes?.content;
  return typeof content === 'string' && content.trim() ? content : null;
};

/** 레거시 parseInt 후 kendo "##,#" — 숫자가 아니면 빈칸 */
export const formatInfluencerNumber = (value: unknown): string => {
  const n = parseInt(String(value ?? ''), 10);
  return Number.isNaN(n) ? '' : n.toLocaleString('en-US');
};

const toInt = (value: unknown): number => parseInt(String(value), 10) || 0;

type PerformanceTotal = {
  contentsCount: number;
  engagement: number;
  tagPriceSum: number;
};

export const sumInfluencerPerformances = (rows: InfluencerPerformance[]) =>
  rows.reduce<PerformanceTotal>(
    (acc, row) => ({
      contentsCount: acc.contentsCount + toInt(row.contentsCount),
      engagement: acc.engagement + toInt(row.engagement),
      tagPriceSum: acc.tagPriceSum + toInt(row.tagPriceSum),
    }),
    { contentsCount: 0, engagement: 0, tagPriceSum: 0 },
  );

/** 임시저장 버튼 없음. 일련번호 없음·조회 중·조회 실패면 상신 막음 */
export const checkInfluencerMonthlyReportBeforeSave = (
  state: InfluencerMonthlyReportState | null | undefined,
): string | null => {
  if (!state?.documentSequence) return INFLUENCER_MONTHLY_MISSING_MESSAGE;
  if (state.loading) return INFLUENCER_MONTHLY_LOADING_MESSAGE;
  if (!state.report) return INFLUENCER_MONTHLY_LOAD_FAILED_MESSAGE;
  return null;
};

export const buildInfluencerMonthlyReportAttributes = (
  state: InfluencerMonthlyReportState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.documentSequence
    ? { documentSequence: state.documentSequence, content }
    : null;
